import { YesNo } from "../../../enum/Enum";

/**
 * OM Pre-Commit Transactions (legacy OMD0025 / uOMD0025.pas).
 *
 * Everything the page and its Layout schema share lives here, so a rename cannot silently break the
 * page's imperative field access (formRefs is keyed by these exact strings).
 */

// cdsQuestions column names, kept as the Delphi names them so the legacy unit stays greppable from
// here. UsePredefinedAccounts is the exception: chkAccount is UI-only in Delphi (not on cdsQuestions),
// and the DTO member name is what the server binds.
export const FIELD = Object.freeze({
    CreateTaxLiability: "CreateTaxLiability",
    AccruedReceivablesAccountRowId: "AccruedReceivablesAccountRowId",
    AccruedPayablesAccountRowId: "AccruedPayablesAccountRowId",
    SalesAccountRowId: "SalesAccountRowId",
    UsePredefinedAccounts: "UsePredefinedAccounts",
    Comment: "Comment",
});

/**
 * "Create Tax Liability" (edtCreateTaxLiab, a TDBFlagCombo) -> @inCreateTaxLiability.
 *
 * A two-entry descriptive dropdown, never free text (Architecture.md rule 3). Codes come from the
 * shared YesNo enum rather than local literals (R2); only the captions are declared here.
 * 'N' is the default - FormCreate seeds cdsQuestionsTaxLiability with it.
 */
export const CREATE_TAX_LIABILITY_OPTIONS = [
    { key: YesNo.Yes, value: "Yes" },
    { key: YesNo.No, value: "No" },
];

export const DEFAULT_CREATE_TAX_LIABILITY = YesNo.No;

/**
 * OMSETUP.PRECOMMITSECURITY: 'N' locks the "Use Predefined Acc." checkbox (the user may not reach the
 * account overrides at all); anything else - including blank - leaves it togglable. That is
 * FormCreate's own test, `if (not varisnull(vr)) and (vr = 'N')`, so a missing setup row falls through
 * to "allowed" exactly as it does in AI8.
 */
export const PRECOMMIT_SECURITY_LOCKED = YesNo.No;

/**
 * Error-log bucket for this screen: @InProgram, the ABERRORSHEADER purge and count, the Preview query
 * and the Print Errors read all key on it. Distinct from the full-commit screen's "OMWEBCOMIT" so the
 * two web screens never clear or display each other's errors (approved decision 1).
 *
 * Mirrors Argos.Core/Constants/OMCommitTransactionConstants.cs -> OMCommitPrograms.PreCommit.
 */
export const PROGRAM = "OMWEBPRECO";

// Filter-field definitions come from Argos.Core/Report/OM/OMCommitTransaction.cs via ReportFactory's
// "OMCOMMITTRANSACTION" case - the SAME field list the full-commit screen uses, because both screens
// filter uOMM0015's qryOMTransactionHeader. Reused rather than copied (Architecture.md R1).
export const REPORT_NAME = "OMCommitTransaction";
export const REPORT_FULL_NAME = "Argos.Report.OM.OMCommitTransaction, Argos.Report.OM";

// Dynamic-form layout identity for the questions panel.
export const LAYOUT_ID = "8C1F5A64-2E93-4D07-BA58-71F6C94D3E20";
export const LAYOUT_NAME = "OMPreCommitTransactionForm";

// One consistent display name everywhere (Architecture.md rule 13): the sidebar entry, the Home
// search entry, the page title and the confirmation dialog's caption all read "Pre-Commit
// Transaction to AC" - the name treeviewSidebar.js already uses, so that is the one the rest follow.
export const PAGE_TITLE = "Pre-Commit Transaction to AC";

/**
 * The accordion's single panel. DevExtreme's Accordion needs a dataSource even when the page draws
 * both the title and the body itself, so this is one inert row - the same shape CommitTransaction.js
 * uses for its own search panel.
 */
export const ACCORDION_PANEL = Object.freeze([Object.freeze({ ID: 1, Name: "Questions" })]);

/*
 * Breathing room left under the filter when the page measures its own --op-chrome. Everything above the
 * filter is measured, so this is the only part that stays a constant: the gap below it.
 */
export const PAGE_BOTTOM_GAP_PX = 12;

// Collapsing the accordion animates over this many ms; matches the sibling commit screen.
export const ACCORDION_ANIMATION_MS = 300;

export const PREVIEW_TITLE = "Preview";
export const PRINT_ERRORS_TITLE = "Print Errors";

// The AB Application Error Log report header the shared TransactionsPrintErrors component renders,
// matching what the full-commit screen passes.
export const ERROR_REPORT_NAME = "ABR003";
export const ERROR_REPORT_FORMAT = "Standard Abecas";

/**
 * Toolbar captions, verbatim from uOMD0025.dfm (accelerator ampersands stripped).
 *
 * Exit is not migrated: it closed the Delphi MDI child, and the web equivalent is navigating away.
 * Same decision as the sibling operation screens.
 */
export const BUTTON = Object.freeze({
    PreCommit: "AC Pre-Commit",
    Preview: "Preview",
    Reset: "Reset",
    PrintErrors: "Print Errors",
});

/**
 * The procedure loops a cursor over every matching transaction and the server allows it 900s
 * (OMCommitConstants.PreCommitCommandTimeoutSeconds). The client must outlast that, or a run that the
 * server is still completing is reported to the user as a failure.
 */
export const REQUEST_TIMEOUT_MS = 20 * 60 * 1000;
